import { AlertCircle, TrendingUp } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui';
import { useRiskScore, useRiskScoreHistory, useScoreLoan } from '@/hooks/use-risk-scoring';
import { fmtDate } from '@/lib/format';
import type { RiskRating } from '@/types';
import { RISK_RATING_CONFIG } from '@/types';

interface Props {
  loanId: string;
}

const riskRatingOrder: RiskRating[] = ['LOW', 'MODERATE', 'ELEVATED', 'HIGH', 'CRITICAL'];

export function RiskScorePanel({ loanId }: Props) {
  const { data: riskScore, isLoading } = useRiskScore(loanId);
  const { data: history } = useRiskScoreHistory(loanId);
  const scoreLoan = useScoreLoan(loanId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Spinner size="lg" className="w-5 h-5 animate-spin mr-2" />
        Loading risk score…
      </div>
    );
  }

  if (!riskScore) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg border border-gray-200">
        <AlertCircle className="w-10 h-10 text-gray-400 mb-3" />
        <p className="text-sm text-gray-600 mb-4">No risk assessment completed</p>
        <Button
          variant="primary"
          onClick={() => scoreLoan.mutate()}
          disabled={scoreLoan.isPending}
          className="w-fit px-4 py-2 text-white rounded-md text-sm font-medium hover: transition-colors disabled:opacity-50"
        >
          {scoreLoan.isPending ? 'Scoring…' : 'Run Risk Assessment'}
        </Button>
      </div>
    );
  }

  const cfg = RISK_RATING_CONFIG[riskScore.riskRating];
  const maxScore = 100;
  const scorePercent = (riskScore.overallScore / maxScore) * 100;

  return (
    <div className="space-y-6">
      {/* Score Header */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-sm font-semibold text-gray-900">Risk Assessment</h3>
          <Button
            variant="secondary"
            onClick={() => scoreLoan.mutate()}
            disabled={scoreLoan.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-blue-500 bg-indigo-50 text-blue-700 rounded-md hover: transition-colors disabled:opacity-50"
          >
            <TrendingUp className="w-3.5 h-3.5" />
            Re-score
          </Button>
        </div>

        <div className="flex items-end gap-8">
          {/* Gauge-like display */}
          <div className="flex-1">
            <div className="relative w-48 h-48 mx-auto mb-4">
              <svg className="w-full h-full" viewBox="0 0 200 200">
                {/* Background arc */}
                <circle
                  cx="100"
                  cy="100"
                  r="90"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                  opacity="0.5"
                />
                {/* Risk colored arcs */}
                {riskRatingOrder.map((rating, idx) => {
                  const startAngle = (idx / riskRatingOrder.length) * 360 - 90;
                  const endAngle = ((idx + 1) / riskRatingOrder.length) * 360 - 90;
                  const startRad = (startAngle * Math.PI) / 180;
                  const endRad = (endAngle * Math.PI) / 180;

                  const x1 = 100 + 90 * Math.cos(startRad);
                  const y1 = 100 + 90 * Math.sin(startRad);
                  const x2 = 100 + 90 * Math.cos(endRad);
                  const y2 = 100 + 90 * Math.sin(endRad);

                  const largeArc = endAngle - startAngle > 180 ? 1 : 0;
                  const pathData = `M ${x1} ${y1} A 90 90 0 ${largeArc} 1 ${x2} ${y2}`;
                  const ratingCfg = RISK_RATING_CONFIG[rating];

                  return (
                    <path
                      key={rating}
                      d={pathData}
                      fill="none"
                      stroke={ratingCfg.color}
                      strokeWidth="8"
                      opacity={rating === riskScore.riskRating ? 1 : 0.3}
                    />
                  );
                })}

                {/* Score pointer */}
                <g>
                  <line
                    x1="100"
                    y1="100"
                    x2={
                      100 +
                      70 * Math.cos((((riskScore.overallScore / 100) * 360 - 90) * Math.PI) / 180)
                    }
                    y2={
                      100 +
                      70 * Math.sin((((riskScore.overallScore / 100) * 360 - 90) * Math.PI) / 180)
                    }
                    stroke="#1f2937"
                    strokeWidth="3"
                  />
                  <circle cx="100" cy="100" r="6" fill="#1f2937" />
                </g>

                {/* Center score */}
                <text
                  x="100"
                  y="95"
                  textAnchor="middle"
                  fontSize="32"
                  fontWeight="bold"
                  fill="#1f2937"
                >
                  {riskScore.overallScore}
                </text>
                <text x="100" y="115" textAnchor="middle" fontSize="12" fill="#6b7280">
                  / 100
                </text>
              </svg>
            </div>

            <div className="text-center">
              <span
                className="inline-block px-3 py-1 rounded-full text-sm font-semibold"
                style={{ color: cfg.color, backgroundColor: cfg.bgColor }}
              >
                {cfg.label}
              </span>
            </div>
          </div>

          {/* Score details */}
          <div className="flex-1">
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-500 mb-1">Overall Score</p>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${scorePercent}%`,
                      backgroundColor: cfg.color,
                    }}
                  />
                </div>
              </div>
              {riskScore.scoredBy && (
                <div>
                  <p className="text-gray-500">Scored By</p>
                  <p className="text-gray-900 font-medium">
                    {riskScore.scoredBy.firstName} {riskScore.scoredBy.lastName}
                  </p>
                </div>
              )}
              <div>
                <p className="text-gray-500">Scored At</p>
                <p className="text-gray-900 font-medium">{fmtDate(riskScore.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {riskScore.recommendation && (
          <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-xs font-semibold text-blue-900 mb-1">Recommendation</p>
            <p className="text-sm text-blue-800">{riskScore.recommendation}</p>
          </div>
        )}
      </div>

      {/* Factor Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-sm font-semibold text-gray-900 mb-4">Risk Factor Breakdown</h4>
        <div className="space-y-4">
          {riskScore.factors.map((factor, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-gray-900">{factor.factor}</p>
                  {factor.detail && <p className="text-xs text-gray-500 mt-0.5">{factor.detail}</p>}
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900">{factor.score.toFixed(1)}</p>
                  <p className="text-xs text-gray-500">
                    Weight: {(factor.weight * 100).toFixed(0)}%
                  </p>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full bg-indigo-500 transition-all"
                  style={{
                    width: `${Math.min((factor.score / 100) * 100, 100)}%`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Score History */}
      {history && history.length > 1 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-4">Score History</h4>
          <div className="space-y-3">
            {history.slice(0, 10).map((score, idx) => {
              const cfg = RISK_RATING_CONFIG[score.riskRating];
              return (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div>
                    <p className="text-gray-900 font-medium">{fmtDate(score.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div
                        className="h-2 rounded-full transition-all"
                        style={{
                          width: `${(score.overallScore / 100) * 100}%`,
                          backgroundColor: cfg.color,
                        }}
                      />
                    </div>
                    <span
                      className="text-xs font-semibold px-2 py-1 rounded-full"
                      style={{ color: cfg.color, backgroundColor: cfg.bgColor }}
                    >
                      {score.overallScore}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
